import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { DataGrid, GridToolbarContainer, GridToolbarDensitySelector, GridToolbarExportContainer, useGridApiContext, useGridApiRef } from "@mui/x-data-grid";
import { Alert, LinearProgress, Snackbar } from "@mui/material";
import { MenuItem } from "@mui/material";
import SearchResultPagination from "./SearchResultPagination";
import { useQuery } from "@tanstack/react-query";
import ms from "ms";
import { createExcel, getTranslationForDataGrid } from "../../utils/Utils";
import Download from "../download/Download";
import { useTranslation } from "react-i18next";
import EmailPopup from "../emailPopUp/EmailPopup";
import { usePushToDataLayer } from "../../hooks/usePushToDataLayer";
import { GTM_EVENTS } from "../../utils/constants";

const SearchResultContext = createContext();
export const SEARCH_RESULT_KEY = "searchResult";
export function useSearchResultContext() {
	const cntx = useContext(SearchResultContext);
	if (!cntx) {
		throw new Error("Should be Child of Search Result");
	}
	return cntx;
}

const LinearProgressSx = {
	mt: 3,
	backgroundColor: "#e5e5e5",
	"& .MuiLinearProgress-bar": {
		backgroundColor: "#0d90c0",
	},
	mb: 3,
};

function DownloadAsZipMenuItem({
	hideMenu, exportToolbar, setSnackbar,
	downloadAll = false, allData, t
}) {
	const apiRef = useGridApiContext();
	const { downloadFns, addDownloadPromise } = useSearchResultContext();
	const pushToDL = usePushToDataLayer();

	if (!exportToolbar?.isrequired) return null;

	const { title, zip } = downloadFns;

	const handleClick = () => {
		let ids = [];

		if (downloadAll) {
			if (allData?.rows?.length > 0) {
				ids = allData?.rows?.map(({ id }) => id) || [];
			} else {
				setSnackbar?.({
					open: true,
					message: t('No PDF files found for the selected bulletin(s). PDFs may not have been uploaded yet.'),
					type: 'warning'
				});
				return;
			}
		} else {
			const selectedRowIds = Array.from(apiRef.current.getSelectedRows().keys()) ?? [];
			const selectedRows = allData?.rows?.length > 0 ?
				allData?.rows?.filter(({ id }) => selectedRowIds.includes(id)) :
				Array.from(apiRef.current.getSelectedRows().values());

			if (selectedRows?.length > 0) {
				ids = selectedRows?.map(({ id }) => id);
			} else {
				setSnackbar?.({
					open: true,
					message: exportToolbar?.warning ?? t('Please select at least one item'),
					type: exportToolbar?.severity ?? 'warning'
				});
				return;
			}
		}

		if (ids?.length > 0) {
			setSnackbar?.({ open: true, message: t('Processing PDF download...'), type: 'info' });
			const downloadPromise = zip(ids?.filter(Boolean));
			addDownloadPromise({ title, fn: downloadPromise }, pushToDL);
			// Update snackbar when promise resolves with warning or success
			downloadPromise.then((result) => {
				const warning = typeof result === 'object' && result !== null ? result.warning : null;
				
				if (warning) {
					setSnackbar?.({ open: true, message: warning, type: 'warning' });
				} else {
					const link = typeof result === 'object' && result !== null ? result.url : result;
					if (link) {
						// Format missing list for display if partial download
						let displayMessage = t('Download ready');
						if (result?.missing && result.missing.length > 0) {
							const missingCount = result.missing.length;
							const previewIds = result.missing.slice(0, 5).join(', ');
							const suffix = missingCount > 5 ? ` ... and ${missingCount - 5} more` : '';
							displayMessage = t('Partial download') + `: ${missingCount} ${t('bulletins without PDFs')} (${previewIds}${suffix})`;
						}
						setSnackbar?.({ open: true, message: displayMessage, type: result?.missing?.length > 0 ? 'warning' : 'success' });
					} else {
						setSnackbar?.({ open: true, message: t('No PDF files found for the selected bulletin(s). PDFs may not have been uploaded yet.'), type: 'warning' });
					}
				}
			}).catch(() => {
				setSnackbar?.({ open: true, message: t('Failed to download PDF'), type: 'error' });
			});
		}

		hideMenu?.();
	};

	return <MenuItem onClick={handleClick}>{exportToolbar?.label ?? '-'}</MenuItem>;
}

function ExcelExportMenuItem({
	exportAll = false, allData, hideMenu,
	exportToolbar, setSnackbar, t
}) {
	const apiRef = useGridApiContext();
	const pushToDL = usePushToDataLayer();
	if (!exportToolbar?.isrequired) return null;

	const handleClick = () => {
		const cols = apiRef.current.getVisibleColumns();
		let data = [];

		if (exportAll) {

			if (allData?.rows?.length > 0) {
				data = allData?.rows || [];
			} else {
				setSnackbar?.({
					open: true,
					message: t('No record found'),
					type: 'info'
				});
				return;
			}
		} else {
			const selectedRowIds = Array.from(apiRef.current.getSelectedRows().keys()) ?? [];
			const selected = allData?.rows?.length > 0 ?
				allData?.rows?.filter(({ id }) => selectedRowIds.includes(id)) :
				Array.from(apiRef.current.getSelectedRows().values());

			if (selected?.length > 0) {
				data = selected;
			} else {
				setSnackbar?.({
					open: true,
					message: exportToolbar?.warning ?? t('Please select at least one item'),
					type: exportToolbar?.severity ?? 'warning'
				});
				return;
			}
		}

		pushToDL(GTM_EVENTS.EXPORT, {
			button_text: exportAll ? 'Export All to Excel' : 'Export Selected to Excel',
			bulletins: data.map(key => key.bulletin_no),
		})
		setSnackbar?.({ open: true, message: t('Processing export...'), type: 'info' });
		createExcel(cols, data?.filter(Boolean));
		hideMenu?.();
	};

	return <MenuItem onClick={handleClick}>{exportToolbar?.label ?? '-'}</MenuItem>;
}

function EmailPdf({
	searchData, hideMenu, setPopEmail, emailAllPdf, t,
	exportToolbar, setSelectedIds, setSnackbar, allData
}) {
	const apiRef = useGridApiContext();

	if (!exportToolbar?.isrequired) return null;

	const handleClick = () => {
		let ids = [];

		if (emailAllPdf) {
			if (allData?.rows?.length > 0) {
				ids = allData?.rows?.map(({ id }) => id) || [];
			} else {
				setSnackbar?.({
					open: true,
					message: t('No record found'),
					type: 'info'
				});
				return;
			}
		} else {
			const selectedRowIds = Array.from(apiRef.current.getSelectedRows().keys()) ?? [];
			const selectedRows = allData?.rows?.length > 0 ?
				allData?.rows?.filter(({ id }) => selectedRowIds.includes(id)) :
				Array.from(apiRef.current.getSelectedRows().values());

			if (selectedRows?.length > 0) {
				ids = selectedRows?.map(({ id }) => id);
			} else {
				setSnackbar?.({
					open: true,
					message: exportToolbar?.warning ?? t('Please select at least one item'),
					type: exportToolbar?.severity ?? 'warning'
				});
				return;
			}
		}

		if (ids?.length > 0) {
			setSelectedIds(ids?.filter(Boolean));
		}

		if (Object.keys(searchData).length > 0) {
			setPopEmail(true);
		}

		hideMenu?.();
	};

	return <MenuItem onClick={handleClick}>{exportToolbar?.label ?? '-'}</MenuItem>;
}

function CustomExportButton({
	csvOptions, printOptions, searchData,
	allData, setPopEmail, exportToolbar,
	setSnackbar, setSelectedIds, t, searchResultData, ...other
}) {
	const isPromoScope = searchResultData?.searchScope === "SBDetailPromo";
	return (
		<GridToolbarExportContainer {...other}>
			{
				!isPromoScope && exportToolbar?.pdf?.download_selected?.isrequired &&
				<DownloadAsZipMenuItem
					setSnackbar={setSnackbar}
					allData={allData}
					exportToolbar={exportToolbar['pdf']?.['download_selected']}
					t={t}
				/>
			}
			{
				!isPromoScope && exportToolbar?.pdf?.download_all?.isrequired &&
				<DownloadAsZipMenuItem
					downloadAll={true}
					setSnackbar={setSnackbar}
					allData={allData}
					exportToolbar={exportToolbar['pdf']?.['download_all']}
					t={t}
				/>
			}
			{
				exportToolbar?.excel?.download_selected?.isrequired &&
				<ExcelExportMenuItem
					setSnackbar={setSnackbar}
					allData={allData}
					exportToolbar={exportToolbar['excel']?.['download_selected']}
					t={t}
				/>
			}
			{
				exportToolbar?.excel?.download_all?.isrequired &&
				<ExcelExportMenuItem
					exportAll={true}
					setSnackbar={setSnackbar}
					allData={allData}
					exportToolbar={exportToolbar['excel']?.['download_all']}
					t={t}
				/>
			}
			{
				!isPromoScope && exportToolbar?.email?.email_selected_pdf?.isrequired &&
				<EmailPdf
					setSnackbar={setSnackbar}
					setSelectedIds={setSelectedIds}
					searchData={searchData}
					setPopEmail={setPopEmail}
					allData={allData}
					exportToolbar={exportToolbar['email']?.['email_selected_pdf']}
					t={t}
				/>
			}
			{
				!isPromoScope && exportToolbar?.email?.email_all_pdf?.isrequired &&
				<EmailPdf
					emailAllPdf={true}
					allData={allData}
					setSnackbar={setSnackbar}
					setSelectedIds={setSelectedIds}
					searchData={searchData}
					setPopEmail={setPopEmail}
					exportToolbar={exportToolbar['email']?.['email_all_pdf']}
					t={t}
				/>
			}
		</GridToolbarExportContainer>
	);
}

function CustomToolbar(props) {
	const { searchData, allData, setPopEmail, exportToolbar, setSnackbar, setSelectedIds, t, searchResultData, ...other } = props;
	return (
		<GridToolbarContainer style={{ justifyContent: "end", paddingBlock: "10px", paddingInline: "20px" }} {...other}>
			<CustomExportButton searchData={searchData}
				allData={allData} setPopEmail={setPopEmail} setSelectedIds={setSelectedIds}
				exportToolbar={exportToolbar} setSnackbar={setSnackbar} t={t}
				searchResultData={searchResultData} />
			<GridToolbarDensitySelector />
		</GridToolbarContainer>
	);
}

function SearchResult({
	columns, searchFn, searchData, downloadFns,
	title, exportToolbar, getAllResult
}) {
	const { t, i18n } = useTranslation();
	const enabled = Object.keys(searchData).length !== 0;
	const [downloadPromises, setDownloadPromises] = useState([]);
	const [popEmail, setPopEmail] = useState(false);
	const [snackbarOpen, setSnackbarOpen] = useState(false);
	const [snackbarMessage, setSnackbarMessage] = useState("");
	const [snackbarSeverity, setSnackbarSeverity] = useState("success");
	const [isProcessing, setIsProcessing] = useState(false);
	const [selectedIds, setSelectedIds] = useState([]);
	const apiRef = useGridApiRef();
	const handleSnackbarClose = () => {
		setSnackbarOpen(false);
		setSnackbarMessage("");
	};
	const pushToDL = usePushToDataLayer();
	const addDownloadPromise = useCallback(
		(dwnPromise) => {
			setDownloadPromises((prevState) => {
				return [...prevState, dwnPromise];
			});
		},
		[setDownloadPromises]
	);
	const clearDownloads = useCallback(() => {
		setDownloadPromises([]);
	}, [setDownloadPromises]);
	const [paginationModel, setPaginationModel] = useState({
		page: 0,
		pageSize: 10,
	});

	// Reset pagination when search is cleared
	useEffect(() => {
		if (!enabled) {
			setPaginationModel({ page: 0, pageSize: 10 });
			setSelectedIds([]);
			// Clear DataGrid row selection
			apiRef.current?.setRowSelectionModel([]);
		}
	}, [enabled]);
	// Support columns as array or function(searchResultData) for dynamic column switching
	const resolveColumns = (data) => typeof columns === 'function' ? columns(data) : columns;
	const [sortModel, setSortModel] = useState(() => {
		const resolvedCols = resolveColumns(null);
		let col = resolvedCols.find((v) => v?.sort) ?? resolvedCols[0];
		return [
			{
				field: col.field,
				sort: col.sort ?? "asc",
			},
		];
	});
	// Serialize the search inputs so an equal-but-new object reference maps to the
	// same query key (prevents duplicate fetches from transient re-renders).
	const searchDataKey = JSON.stringify(searchData);
	const sortModelKey = JSON.stringify(sortModel);

	const { isFetching: searchFetching, data: searchResultDataRaw, isError: searchResultError } = useQuery({
		queryFn: () => searchFn(paginationModel, sortModel, searchData, pushToDL),
		queryKey: [title, SEARCH_RESULT_KEY, paginationModel, sortModelKey, searchDataKey],
		placeholderData: (previousData) => previousData,
		enabled,
		gcTime: ms("1h"),
		staleTime: ms("1h"),
	});

	// When search is cleared (enabled=false), don't show stale placeholder data
	const searchResultData = enabled ? searchResultDataRaw : undefined;

	const isGetAllResult = Boolean(getAllResult);
	const { isFetching: allFetching, data: allDataRaw, isError: allSearchError } = useQuery({
		queryFn: () => getAllResult?.(sortModel, searchData, false, false, false, pushToDL) ?? Promise.resolve(undefined),
		queryKey: [title, SEARCH_RESULT_KEY, sortModelKey, searchDataKey],
		placeholderData: (previousData) => previousData,
		enabled: enabled && isGetAllResult
	});

	// When search is cleared, don't show stale all-data either
	const allData = enabled ? allDataRaw : undefined;

	useEffect(() => {
		if(searchResultError || allSearchError){
		setSnackbarMessage("Something went wrong. Please try again later.");
		setSnackbarSeverity("error");
		setSnackbarOpen(true);
	}
	}, [searchResultError, allSearchError])

	const rowCountRef = useRef(searchResultData?.totalRowCount || 0);

	const rowCount = React.useMemo(() => {
		if (!enabled) {
			rowCountRef.current = 0;
			return 0;
		}
		if (searchResultData?.totalRowCount !== undefined) {
			rowCountRef.current = searchResultData.totalRowCount;
		}
		return rowCountRef.current;
	}, [searchResultData?.totalRowCount, enabled]);
	
	const setSnackbar = useCallback(({ open, message, type }) => {
		setSnackbarMessage(message);
		setSnackbarSeverity(type);
		setSnackbarOpen(Boolean(open));
	}, []);

	const CustomToolbarWrapper = (props) => {
		if(exportToolbar) {
			return <CustomToolbar {...props} searchData={searchData} allData={allData}
				setPopEmail={setPopEmail} setSelectedIds={setSelectedIds}
				exportToolbar={exportToolbar} setSnackbar={setSnackbar} t={t}
				searchResultData={searchResultData} />;
		}

		return null;
	};

	return (
		<>
			{searchFetching ? <LinearProgress sx={LinearProgressSx} /> : <hr style={{ marginTop: "26px" }} className='horizontal-ruler' />}
			<SearchResultContext.Provider value={{ downloadFns, addDownloadPromise, downloadPromises, clearDownloads }}>
				<div className='search__results'>
					<div className='search__heading'>{t("Results")}</div>
					<DataGrid
						apiRef={apiRef}
						autoHeight
						disableRowSelectionOnClick
						keepNonExistentRowsSelected
						rows={searchResultData?.rows}
						columns={resolveColumns(searchResultData)}
						checkboxSelection={Boolean(exportToolbar)}
						paginationMode='server'
						loading={searchFetching}
						pageSizeOptions={[5, 10, 25]}
						onPaginationModelChange={setPaginationModel}
						paginationModel={paginationModel}
						rowCount={rowCount}
						disableColumnMenu
						sortingMode='server'
						onSortModelChange={useCallback((newSortModel) => setSortModel(newSortModel), [])}
						sortModel={sortModel}
						slots={{ toolbar: CustomToolbarWrapper, pagination: SearchResultPagination }}
						localeText={getTranslationForDataGrid(i18n.resolvedLanguage)}
					/>
				</div>
				{downloadPromises.length !== 0 && <Download />}
			</SearchResultContext.Provider>
			<Snackbar
				open={snackbarOpen}
				{...(isProcessing ? {} : {autoHideDuration: 6000})}
				onClose={handleSnackbarClose}
				anchorOrigin={{ vertical: "top", horizontal: "right" }}
				sx={{ height: "20%" }}>
				<Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: "100%", fontSize: "15px" }} variant='filled'>
					{snackbarMessage}
				</Alert>
			</Snackbar>

			<EmailPopup
				active={popEmail}
				setClose={setPopEmail}
				customStyles={{ position: "fixed" }}
				searchData={searchData}
				sortModel={sortModel}
				setSnackbarOpen={setSnackbarOpen}
				setSnackbarMessage={setSnackbarMessage}
				setSnackbarSeverity={setSnackbarSeverity}
				setIsProcessing={setIsProcessing}
				getAllResult={getAllResult}
				selectedIds={selectedIds}
				subTitle={
					t(`To email the ${exportToolbar?.type ?? 'items'}, 
						please enter the recipient's email address(es) below.`)
				}
			/>

		</>
	);
}

export default SearchResult;
